import { DocumentFeedItemType } from '../../documents/infra/types';
import { DocumentModelProps } from '../../documents/models/Documents.model';
import {
  ApprovalsUtil,
  AttachmentUtil,
} from '../../documents/utils/DocumentsUtil';
import { EventFeedItemType } from '../../events/infra/types';
import { EventModelProps } from '../../events/models/Events.model';
import { EventDateTimeUtil } from '../../events/utils/EventUtils';

import { MembersUtil } from '../../members/utils/MembersUtil';
import { TagUtil } from '../../tags/utils/TagUtil';
import { ViewItemResponse } from '../infra/types/FeedItemViews';
import {
  FeedItemModelPropsUnion,
  FeedItemViewBaseModel,
  FeedItemViewModel,
} from '../models/FeedItemView.model';

import { JobsFeedItemType } from '../../jobs/infra/types';
import { JobsModelProps } from '../../jobs/models/Jobs.model';
import { NewsFeedItemType } from '../../news/infra/types';
import { NewsModelProps } from '../../news/models/News.model';
import { FormFeedItemType } from '../../form/types';
import { FormsModelProps } from '../../form/models/Forms.model';
import { FormNodeUtil } from '../../form/utils/FormNodeUtils';
import { BasicGroupUtil } from '../../groups/utils/BasicGroupUtil';

export class FeedItemViewUtils {
  public static mapToViewModel(
    response: ViewItemResponse,
  ): FeedItemViewModel<FeedItemModelPropsUnion> {
    const { data: dto } = response;

    const base: FeedItemViewBaseModel = {
      id: dto.id,
      headline: dto.headline,
      tagline: dto.tagline,
      coverImage: dto.coverImage,
      access: dto.access,
      status: dto.status,
      tags: dto.tags ? dto.tags.map((tag) => TagUtil.mapToViewModel(tag)) : [],
      publishedOn: dto.publishedOn,
      citationRequired: dto.citationRequired,
      feedType: dto.feedItemType,
      recipients: dto.recipients
        ? dto.recipients.map((member) => MembersUtil.mapToViewModel(member))
        : [],
      groups: dto.groups
        ? dto.groups.map((group) => BasicGroupUtil.mapToViewModel(group))
        : [],
    };

    switch (dto.feedItemType) {
      case 'DocumentItem': {
        const feedItem = dto.feedItem as DocumentFeedItemType;
        const props: DocumentModelProps = {
          attachment: AttachmentUtil.maptToViewModel(feedItem.attachment),
          approval: feedItem.approval
            ? ApprovalsUtil.maptToViewModel(feedItem.approval)
            : null,
        };

        const feedItemView: FeedItemViewModel<DocumentModelProps> = {
          ...base,
          data: { ...props },
        };

        return feedItemView;
      }
      case 'EventItem': {
        const feedItem = dto.feedItem as EventFeedItemType;
        const props: EventModelProps = {
          body: feedItem.body,
          location: feedItem.location,
          dateTimes: feedItem.dateTimes
            ? feedItem.dateTimes.map((item) =>
                EventDateTimeUtil.mapToViewModel(item),
              )
            : [],
        };

        const feedItemView: FeedItemViewModel<EventModelProps> = {
          ...base,
          data: { ...props },
        };

        return feedItemView;
      }
      case 'FormItem': {
        const feedItem = dto.feedItem as FormFeedItemType;
        const props: FormsModelProps = {
          emails: feedItem.emails,
          formNodes: feedItem.formNodes
            ? feedItem.formNodes.map((node) =>
                FormNodeUtil.mapToViewModel(node),
              )
            : [],
        };

        const feedItemView: FeedItemViewModel<FormsModelProps> = {
          ...base,
          data: { ...props },
        };

        return feedItemView;
      }
      case 'JobItem': {
        const feedItem = dto.feedItem as JobsFeedItemType;

        const props: JobsModelProps = {
          body: feedItem.body,
          closing_date: feedItem.closing_date,
          employer: feedItem.employer,
          location: feedItem.location,
          type: feedItem.type,
          external_url: feedItem.external_url,
          position: feedItem.position,
          qualifications: feedItem.qualifications,
          responsibilities: feedItem.responsibilities,
          salary: feedItem.salary,
          contact_info: feedItem.contact_info,
        };

        const feedItemView: FeedItemViewModel<JobsModelProps> = {
          ...base,
          data: { ...props },
        };

        return feedItemView;
      }
      case 'NewsItem': {
        const feedItem = dto.feedItem as NewsFeedItemType;
        const props: NewsModelProps = {
          body: feedItem.body,
        };

        const feedItemView: FeedItemViewModel<NewsModelProps> = {
          ...base,
          data: { ...props },
        };

        return feedItemView;
      }
    }
  }
}
